import React, { useRef, useState, useCallback } from 'react';
import styles from './DropZone.module.scss';

interface Props {
  onFile: (file: File) => void;
  loading: boolean;
}

export default function DropZone({ onFile, loading }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    // reset input so same file can be re-uploaded
    e.target.value = '';
  };

  const handleSample = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${process.env.PUBLIC_URL}/Test-Valid.csv`);
      if (!response.ok) throw new Error(`Failed to load sample file: ${response.statusText}`);
      const blob = await response.blob();
      const file = new File([blob], 'test-valid.csv', { type: 'text/csv' });
      onFile(file);
    } catch (err) {
      console.error('Could not load sample CSV:', err);
      alert('Could not load sample file. Make sure test-valid.csv is in the public/assets folder.');
    }
  };

  return (
    <>
    <div
      className={`${styles.dropZone} ${dragging ? styles.dragging : ''} ${loading ? styles.loading : ''}`}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !loading && inputRef.current?.click()}
      role="button"
      aria-label="Upload CSV file"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className={styles.fileInput}
        onChange={handleChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      <span className={styles.icon} aria-hidden="true">
        {loading ? '⏳' : '📄'}
      </span>
      <p className={styles.title}>
        {loading ? 'Parsing file…' : 'Drop your CSV file here'}
      </p>
      <p className={styles.subtitle}>
        {loading
          ? 'Please wait'
          : <>or click to browse &nbsp;·&nbsp; <strong>Name</strong> and <strong>Date</strong> columns required &nbsp;·&nbsp; Max 5 MB</>
        }
      </p>
    </div>
    {!loading && (
        <button
          className={styles.sampleBtn}
          onClick={handleSample}
          aria-label="Load sample CSV data"
        >
          📋 &nbsp;Load sample data
        </button>
      )}
    </>
  );
}
